/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { usePaginatedUtterances } from 'src/luis/projects/utterances/hooks/usePaginatedUtterances';
import { useUtteranceActions } from 'src/luis/projects/utterances/hooks/useUtteranceActions';
import { useUtterances, UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { UtteranceTableViewOptions } from 'src/luis/projects/utterances/types/UtteranceTableViewOptions';
import { isConfirmAllPredictionsDisabled } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { EditUtteranceModal } from 'src/luis/verticals/entityDetails/components/entityExamples/EditUtteranceModal';
import { EntityUtterancesTable } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityUtterancesTable';
import { EntityUtterancesTableCommandBar } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityUtterancesTableCommandBar';

export type EntityExamplesProps = {
    entityStore: EntityStore;
};

export type EntityUtteranceTableViewOptions = Pick<
    UtteranceTableViewOptions,
    'hideEntityNames' | 'hidePredictions' | 'hideRegexAndPrebuilt' | 'hideSubEntities'
>;

const messages = defineMessages({
    deleteUtteranceModalTitle: {
        id: 'newEntityDetails.entityExamples.deleteUtteranceModalTitle',
        defaultMessage: 'Delete selected utterance(s)?'
    },
    deleteUtteranceModalDescription: {
        id: 'newEntityDetails.entityExamples.deleteUtteranceModalDescription',
        defaultMessage: 'Are you sure you want to delete the selected utterance(s)?'
    },
    announceDeleteUtterance: {
        id: 'newEntityDetails.entityExamples.announceDeleteUtterance',
        defaultMessage: 'Utterance(s) successfully deleted.'
    },
    announceEditUtterance: {
        id: 'newEntityDetails.entityExamples.announceEditUtterance',
        defaultMessage: 'Utterance successfully edited.'
    }
});

/**
 * The component displaying the utterances for a given entity.
 */
export const EntityExamples = observer((props: EntityExamplesProps) => {
    const { entityStore } = props;

    const { rootStore, uiStore, dispatcher } = useGlobalState();
    const { localize } = useLocalization();
    const { announcedComponent, announceAction } = useAnnounced();

    const { selectedItems, selection } = useSelection<UtterancePredictionsStore>();
    const [isDeleteModalHidden, setIsDeleteModalHidden] = React.useState(true);

    const doNotShowEditUtteranceModal = uiStore.isEditUtteranceDisclaimerModalHidden;
    const [isEditUtteranceModalHidden, setIsEditUtteranceModalHidden] = React.useState(true);

    const [query, setQuery] = React.useState('');
    const [utteranceIdBeingEdited, setUtteranceIdBeingEdited] = React.useState<LuisId>(null);
    const [utteranceBeingEditedData, setUtteranceBeingEditedData] = React.useState<{ text: string; id: LuisId }>(null);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canDeleteData } = mapUserRoleToUserActions(userRole);

    const viewOptions = uiStore.utterancesViewOptions;

    const updateViewOptions = (utterancesViewOption: Partial<EntityUtteranceTableViewOptions>) =>
        dispatcher.dispatch('toggleUtterancesViewOption', { utterancesViewOption });

    const isChunking = Boolean(query.trim());

    const {
        isRTL,
        currentAppVersionStore: { entityStoreHouse }
    } = rootStore.currentLuisAppStore;

    const { intents, isLoading, pageIndex, totalSize, pageSize, utterances } = useUtterances({
        model: entityStore,
        isChunking
    });

    const { deleteUtterances, confirmEntityPredictions, editUtterance, changePageIndex } = useUtteranceActions({
        model: entityStore
    });

    const orderedUtterances = orderBy(utterances, ({ utteranceStore }: UtterancePredictionsStore) => utteranceStore.createdDate, ['desc']);

    const filteredUtterances = substringSearchStrategy(
        orderedUtterances,
        query,
        null,
        (utterance, q) => utterance.utteranceStore.text.toLocaleLowerCase().indexOf(q) !== -1
    );

    const { items: pageItems, pageCount, currentPage, onPageChange } = usePaginatedUtterances({
        utterances: filteredUtterances,
        isChunking,
        changePageIndex,
        pageIndex,
        pageSize,
        totalSize
    });

    const deleteTracker = useProgressTracker({
        onComplete: () => {
            selection.setAllSelected(false);
            setIsDeleteModalHidden(true);
            announceAction(localize(messages.announceDeleteUtterance));
        }
    });

    const editUtteranceTracker = useProgressTracker({
        onComplete: () => {
            selection.setAllSelected(false);
            setUtteranceIdBeingEdited(null);
            setUtteranceBeingEditedData(null);
            setIsEditUtteranceModalHidden(true);
            announceAction(localize(messages.announceEditUtterance));
        }
    });

    const handleEditUtteranceSubmit = (text: string) => {
        if (doNotShowEditUtteranceModal) {
            handleEditUtterance(text, utteranceIdBeingEdited);
        } else {
            setUtteranceBeingEditedData({ text, id: utteranceIdBeingEdited });
            setIsEditUtteranceModalHidden(false);
        }
    };

    const handleEditUtteranceModalSubmit = (doNotShowAgain: boolean) => {
        handleEditUtterance(utteranceBeingEditedData.text, utteranceBeingEditedData.id);
        if (doNotShowAgain) {
            dispatcher.dispatch('hideEditUtteranceDisclaimerModal');
        }
    };

    const handleEditUtterance = (text: string, utteranceId: LuisId) => {
        const intentId = utterances.find(utterance => utterance.utteranceStore.id === utteranceId).utteranceStore.labeledIntentId;
        editUtterance(
            utteranceId,
            text,
            editUtteranceTracker,
            intents.find(({ id }) => id === intentId)
        );
    };

    /**
     * Maps the selected items to array of ids
     * for faster accessibility.
     */
    const getSelectedIds = (items: UtterancePredictionsStore[]) => items.map(item => item.utteranceStore.id);

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <EntityUtterancesTableCommandBar
                selectedItems={selectedItems}
                query={query}
                onQueryChange={setQuery}
                viewOptions={viewOptions}
                onChangeViewOptions={updateViewOptions}
                onConfirmPredictions={() => confirmEntityPredictions(getSelectedIds(selectedItems), 'commandBar')}
                onDeleteUtterances={() => setIsDeleteModalHidden(false)}
                onEditUtterance={() => setUtteranceIdBeingEdited(getSelectedIds(selectedItems)[0])}
                isConfirmPredictionsDisabled={isConfirmAllPredictionsDisabled(selectedItems, entityStoreHouse.items)}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <EntityUtterancesTable
                items={pageItems}
                isLoading={isLoading}
                selection={selection}
                isRTL={isRTL}
                areItemsFiltered={Boolean(query)}
                overallItemsCount={filteredUtterances.length}
                onConfirmPrediction={() => confirmEntityPredictions(getSelectedIds(selectedItems), 'utteranceRow')}
                onDelete={() => setIsDeleteModalHidden(false)}
                onDeleteKeyUp={() => canDeleteData && setIsDeleteModalHidden(false)}
                onEdit={val => setUtteranceIdBeingEdited(val ? getSelectedIds(selectedItems)[0] : null)}
                onSubmitEditInput={handleEditUtteranceSubmit}
                utteranceIdBeingEdited={utteranceIdBeingEdited}
                viewOptions={viewOptions}
                userRole={userRole}
            />

            {announcedComponent}

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => onPageChange(p.selected)} />

            <DeletePromptModal
                hidden={isDeleteModalHidden}
                onDelete={() => deleteUtterances(getSelectedIds(selectedItems), deleteTracker)}
                onDismiss={() => setIsDeleteModalHidden(true)}
                disabled={deleteTracker.isRunning}
                dialogContentProps={{
                    title: localize(messages.deleteUtteranceModalTitle),
                    subText: localize(messages.deleteUtteranceModalDescription)
                }}
            />

            <EditUtteranceModal
                hidden={isEditUtteranceModalHidden}
                disabled={editUtteranceTracker.isRunning}
                onDismiss={() => setIsEditUtteranceModalHidden(true)}
                onSubmit={handleEditUtteranceModalSubmit}
            />
        </Stack>
    );
});
